/**
 * AdminProductFormPage - Admin product form page
 * [PHASE 3] Advanced E-commerce Features
 *
 * Features:
 * - Create new products
 * - Edit existing products
 * - Form validation
 * - Category selection
 * - Image management
 * - Price and stock management
 * - Status control (draft/publish)
 * - Markdown description editor with live preview
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import MarkdownEditor from '../../../components/admin/MarkdownEditor';
import {
  getProductById,
  createProduct,
  updateProduct,
  getProductCategories,
} from '../../../services/productsApi';
import type { ProductCategory } from '../../../types';
import './AdminProductFormPage.css';

interface FormData {
  sku: string;
  title: string;
  description: string;
  productCategory: string;
  regularPrice: string;
  salePrice: string;
  stockQuantity: string;
  status: 'publish' | 'draft' | 'pending' | 'private' | 'archived';
  featuredImageUrl: string;
  imageUrls: string[];
  featured: boolean;
}

const AdminProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formData, setFormData] = useState<FormData>({
    sku: '',
    title: '',
    description: '',
    productCategory: '',
    regularPrice: '',
    salePrice: '',
    stockQuantity: '',
    status: 'draft',
    featuredImageUrl: '',
    imageUrls: [],
    featured: false,
  });

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await getProductCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setToast({ type: 'error', message: '無法載入分類資料' });
    }
  };

  const fetchProduct = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getProductById(id);
      if (response.success) {
        const product = response.data;
        // Handle comma-separated categories - take first one as default
        const primaryCategory = product.productCategory
          ? product.productCategory.split(',').map(c => c.trim()).filter(c => c)[0] || ''
          : '';
        setFormData({
          sku: product.sku || '',
          title: product.title || '',
          description: product.description || '',
          productCategory: primaryCategory,
          regularPrice: product.regularPrice?.toString() || '',
          salePrice: product.salePrice?.toString() || '',
          stockQuantity: product.stockQuantity?.toString() || '',
          status: product.status || 'draft',
          featuredImageUrl: product.featuredImageUrl || '',
          imageUrls: product.imageUrls || [],
          featured: product.featured || false,
        });
      } else {
        setToast({ type: 'error', message: '無法載入商品資料' });
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      setToast({ type: 'error', message: '無法載入商品資料' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU 為必填欄位';
    }

    if (!formData.title.trim()) {
      newErrors.title = '商品名稱為必填欄位';
    }

    if (!formData.productCategory) {
      newErrors.productCategory = '請選擇分類';
    }

    if (formData.regularPrice && isNaN(Number(formData.regularPrice))) {
      newErrors.regularPrice = '請輸入有效的售價';
    }

    if (formData.salePrice && isNaN(Number(formData.salePrice))) {
      newErrors.salePrice = '請輸入有效的特價';
    }

    if (formData.stockQuantity && isNaN(Number(formData.stockQuantity))) {
      newErrors.stockQuantity = '請輸入有效的庫存數量';
    }

    if (formData.salePrice && formData.regularPrice && Number(formData.salePrice) > Number(formData.regularPrice)) {
      newErrors.salePrice = '特價不能高於售價';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        sku: formData.sku.trim(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        productCategory: formData.productCategory,
        regularPrice: formData.regularPrice ? Number(formData.regularPrice) : undefined,
        salePrice: formData.salePrice ? Number(formData.salePrice) : undefined,
        stockQuantity: formData.stockQuantity ? Number(formData.stockQuantity) : undefined,
        status: formData.status as 'publish' | 'draft' | 'pending' | 'private',
        featuredImageUrl: formData.featuredImageUrl || undefined,
        imageUrls: formData.imageUrls,
        featured: formData.featured,
      };

      if (isEdit && id) {
        await updateProduct(id, payload);
        setToast({ type: 'success', message: '商品更新成功' });
      } else {
        await createProduct(payload);
        setToast({ type: 'success', message: '商品建立成功' });
      }

      setTimeout(() => {
        navigate('/admin/products');
      }, 500);
    } catch (error) {
      console.error('Failed to save product:', error);
      const errorMessage = error instanceof Error ? error.message : (isEdit ? '更新失敗，請稍後再試' : '建立失敗，請稍後再試');
      setToast({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddImageUrl = () => {
    const url = prompt('請輸入圖片 URL:');
    if (url && url.trim()) {
      setFormData((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, url.trim()],
      }));
    }
  };

  const handleRemoveImageUrl = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index),
    }));
  };

  const handleCloseToast = () => {
    setToast(null);
  };

  if (loading && isEdit) {
    return (
      <DesktopLayout>
        <div className="admin-product-form-page">
          <div className="admin-product-form-page__loading">
            <div className="admin-product-form-page__spinner"></div>
            <p>載入中...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="admin-product-form-page">
        {/* Header */}
        <header className="admin-product-form-page__header">
          <div className="admin-product-form-page__title-section">
            <h1 className="admin-product-form-page__title">
              {isEdit ? '編輯商品' : '新增商品'}
            </h1>
            <p className="admin-product-form-page__subtitle">
              {isEdit ? '更新商品資訊' : '建立新的商品'}
            </p>
          </div>
        </header>

        {/* Toast Notification */}
        {toast && (
          <div className={`admin-product-form-page__toast admin-product-form-page__toast--${toast.type}`}>
            <span className="admin-product-form-page__toast-message">{toast.message}</span>
            <button
              className="admin-product-form-page__toast-close"
              onClick={handleCloseToast}
              type="button"
            >
              ×
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="admin-product-form-page__form">
          {/* Basic Information */}
          <section className="admin-product-form-page__section">
            <h2 className="admin-product-form-page__section-title">基本資訊</h2>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="sku">
                SKU <span className="admin-product-form-page__required">*</span>
              </label>
              <input
                id="sku"
                type="text"
                className={`admin-product-form-page__input ${errors.sku ? 'admin-product-form-page__input--error' : ''}`}
                value={formData.sku}
                onChange={(e) => handleChange('sku', e.target.value)}
                placeholder="請輸入 SKU"
              />
              {errors.sku && <span className="admin-product-form-page__error">{errors.sku}</span>}
            </div>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="title">
                商品名稱 <span className="admin-product-form-page__required">*</span>
              </label>
              <input
                id="title"
                type="text"
                className={`admin-product-form-page__input ${errors.title ? 'admin-product-form-page__input--error' : ''}`}
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="請輸入商品名稱"
              />
              {errors.title && <span className="admin-product-form-page__error">{errors.title}</span>}
            </div>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="description">
                商品描述
                <span className="admin-product-form-page__label-hint">
                  (支援 Markdown 格式，即時預覽)
                </span>
              </label>
              <MarkdownEditor
                value={formData.description}
                onChange={(value) => handleChange('description', value)}
                placeholder="請輸入商品描述（支援 Markdown）

## 商品特色
- 特色一
- 特色二

### 規格說明
| 項目 | 規格 |
|------|------|
| 尺寸 | 10 x 20 cm |
| 材質 | 棉質 |"
                height="400px"
              />
            </div>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="category">
                分類 <span className="admin-product-form-page__required">*</span>
              </label>
              <select
                id="category"
                className={`admin-product-form-page__select ${errors.productCategory ? 'admin-product-form-page__input--error' : ''}`}
                value={formData.productCategory}
                onChange={(e) => handleChange('productCategory', e.target.value)}
              >
                <option value="">請選擇分類</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.productCategory && (
                <span className="admin-product-form-page__error">{errors.productCategory}</span>
              )}
            </div>
          </section>

          {/* Pricing & Stock */}
          <section className="admin-product-form-page__section">
            <h2 className="admin-product-form-page__section-title">價格與庫存</h2>

            <div className="admin-product-form-page__row">
              <div className="admin-product-form-page__field admin-product-form-page__field--half">
                <label className="admin-product-form-page__label" htmlFor="regularPrice">
                  售價
                </label>
                <input
                  id="regularPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  className={`admin-product-form-page__input ${errors.regularPrice ? 'admin-product-form-page__input--error' : ''}`}
                  value={formData.regularPrice}
                  onChange={(e) => handleChange('regularPrice', e.target.value)}
                  placeholder="0.00"
                />
                {errors.regularPrice && (
                  <span className="admin-product-form-page__error">{errors.regularPrice}</span>
                )}
              </div>

              <div className="admin-product-form-page__field admin-product-form-page__field--half">
                <label className="admin-product-form-page__label" htmlFor="salePrice">
                  特價
                </label>
                <input
                  id="salePrice"
                  type="number"
                  min="0"
                  step="0.01"
                  className={`admin-product-form-page__input ${errors.salePrice ? 'admin-product-form-page__input--error' : ''}`}
                  value={formData.salePrice}
                  onChange={(e) => handleChange('salePrice', e.target.value)}
                  placeholder="0.00"
                />
                {errors.salePrice && <span className="admin-product-form-page__error">{errors.salePrice}</span>}
              </div>
            </div>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="stockQuantity">
                庫存數量
              </label>
              <input
                id="stockQuantity"
                type="number"
                min="0"
                className={`admin-product-form-page__input ${errors.stockQuantity ? 'admin-product-form-page__input--error' : ''}`}
                value={formData.stockQuantity}
                onChange={(e) => handleChange('stockQuantity', e.target.value)}
                placeholder="0"
              />
              {errors.stockQuantity && (
                <span className="admin-product-form-page__error">{errors.stockQuantity}</span>
              )}
            </div>
          </section>

          {/* Images */}
          <section className="admin-product-form-page__section">
            <h2 className="admin-product-form-page__section-title">圖片</h2>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="featuredImageUrl">
                特色圖片 URL
              </label>
              <input
                id="featuredImageUrl"
                type="url"
                className="admin-product-form-page__input"
                value={formData.featuredImageUrl}
                onChange={(e) => handleChange('featuredImageUrl', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              {formData.featuredImageUrl && (
                <div className="admin-product-form-page__image-preview">
                  <img src={formData.featuredImageUrl} alt="特色圖片預覽" />
                </div>
              )}
            </div>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label">其他圖片</label>
              <div className="admin-product-form-page__image-list">
                {formData.imageUrls.map((url, index) => (
                  <div key={index} className="admin-product-form-page__image-item">
                    <img src={url} alt={`圖片 ${index + 1}`} />
                    <button
                      type="button"
                      className="admin-product-form-page__image-remove"
                      onClick={() => handleRemoveImageUrl(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="admin-product-form-page__image-add"
                  onClick={handleAddImageUrl}
                >
                  + 新增圖片
                </button>
              </div>
            </div>
          </section>

          {/* Settings */}
          <section className="admin-product-form-page__section">
            <h2 className="admin-product-form-page__section-title">設定</h2>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__label" htmlFor="status">
                狀態
              </label>
              <select
                id="status"
                className="admin-product-form-page__select"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value as 'publish' | 'draft')}
              >
                <option value="draft">草稿</option>
                <option value="publish">已發布</option>
              </select>
            </div>

            <div className="admin-product-form-page__field">
              <label className="admin-product-form-page__checkbox-label">
                <input
                  type="checkbox"
                  className="admin-product-form-page__checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleChange('featured', e.target.checked)}
                />
                <span>設為特色商品</span>
              </label>
            </div>
          </section>

          {/* Actions */}
          <div className="admin-product-form-page__actions">
            <button
              type="submit"
              className="admin-product-form-page__submit-btn"
              disabled={loading}
            >
              {loading ? '儲存中...' : isEdit ? '更新商品' : '建立商品'}
            </button>
            <button
              type="button"
              className="admin-product-form-page__cancel-btn"
              onClick={() => navigate('/admin/products')}
              disabled={loading}
            >
              取消
            </button>
          </div>
        </form>
      </div>
    </DesktopLayout>
  );
};

export default AdminProductFormPage;
